import { type BasisPoints, type Kopecks, kopecks, workDaysOfWeekRange } from '@glory/kernel';

import type { IsoDate } from './model.ts';
import type { StudioSnapshot } from './snapshot.ts';

/**
 * Договор с клиентом из КП (раздел 5 спецификации подпроекта 4): заявка по оферте, спецификация — приложение № 1 и
 * счёт на предоплату. Всё берётся из снимка принятой версии КП и не пересчитывается.
 */

/** Когда платится доля графика — те же моменты, что в КП: при подписании, со стартом этапа, при запуске, ежемесячно. */
export type PaymentMoment = 'signing' | 'stage_start' | 'launch' | 'monthly';

/** Доля графика оплат в рублях из снимка КП: суммы по пакету сходятся с его ценой до копейки. */
export interface AcceptedPayment {
  readonly label: string;
  readonly trigger: PaymentMoment;
  readonly share: BasisPoints;
  readonly amount: Kopecks;
  /** Этап, со стартом которого платится доля (`stage_start`). */
  readonly stage: string | null;
}

/** Этап выбранного пакета глазами клиента: название, результат, недели и рабочие дни (с единицы, включительно). */
export interface AcceptedStage {
  readonly title: string;
  readonly result: string | null;
  readonly weeks: { readonly from: number; readonly to: number } | null;
  /** Рабочие дни этапа по плану; у КП, отправленных до 2026-10-10, — нет (считаются из недель). */
  readonly days?: { readonly from: number; readonly to: number } | null;
}

/**
 * Принятая версия КП (порт `DocumentProposals`): текущая отправленная версия, по которой последнее решение клиента —
 * «выбран» (на странице клиента или «со слов клиента»). Только то, что видел клиент: цена, срок, «что входит»,
 * этапы, график оплат, НДС, поддержка.
 */
export interface AcceptedProposal {
  readonly versionId: string;
  /** `КП-2026-0012/2`. */
  readonly label: string;
  readonly issuedOn: IsoDate;
  readonly projectTitle: string;
  /** Выбранный пакет; у КП «только поддержка» — null. */
  readonly package: {
    readonly key: string;
    readonly name: string;
    /** «≈ N недель» — вверх до недели; null — у КП без этапов. */
    readonly termWeeks: number | null;
    /** Срок по плану в рабочих днях; у КП, отправленных до 2026-10-10, — нет (считается из недель). */
    readonly termDays?: number | null;
    readonly includes: readonly string[];
  } | null;
  readonly stages: readonly AcceptedStage[];
  /** График оплат выбранного пакета; пусто — графика в КП нет. */
  readonly payments: readonly AcceptedPayment[];
  /** Тариф поддержки, если клиент его выбрал. */
  readonly support: {
    readonly name: string;
    readonly hoursPerMonth: number;
    readonly billing: 'month' | 'year';
    readonly price: Kopecks;
  } | null;
  /** Цены КП с НДС или без: пометка под суммами. */
  readonly vat: { readonly included: boolean; readonly rate: BasisPoints | null };
  /** Итог выбора — бюджет сделки и сумма заявки: цена пакета (у почасовой — нижняя граница), у «только поддержки» — тариф. */
  readonly total: Kopecks;
}

/** НДС для пометки «НДС не облагается» / «В том числе НДС 20 %»: из снимка КП, а не из сегодняшнего профиля. */
export function vatOf(
  vat: AcceptedProposal['vat'],
): { readonly kind: 'none' } | { readonly kind: 'vat'; readonly rate: number } {
  return vat.included && vat.rate !== null && vat.rate > 0
    ? { kind: 'vat', rate: vat.rate }
    : { kind: 'none' };
}

/** Доля графика заявки: её место в графике (с нуля) и сумма — у счёта и акта в `payment_part`. */
export interface OrderPayment extends AcceptedPayment {
  readonly index: number;
}

/** Заявка из снимка КП: сумма, график по долям и доли «при подписании» — по ним счёт на предоплату. */
export interface OrderTerms {
  readonly amount: Kopecks;
  readonly payments: readonly OrderPayment[];
  readonly prepayments: readonly OrderPayment[];
  /** Сумма долей графика равна сумме заявки до копейки (иначе график в документы не идёт — он бы врал). */
  readonly scheduleMatches: boolean;
}

/**
 * Условия заявки — из снимка КП без пересчёта: сумма — итог выбора, график — доли пакета как есть. Если доли не
 * сходятся с суммой (почасовая сделка с «от–до»), график в заявку не попадает, и предоплаты нет.
 */
export function orderTerms(accepted: AcceptedProposal): OrderTerms {
  const payments = accepted.payments.map((part, index) => ({ ...part, index }));
  const sum = payments.reduce((acc, part) => acc + part.amount, 0);
  const scheduleMatches = payments.length > 0 && sum === accepted.total;
  return {
    amount: accepted.total,
    payments: scheduleMatches ? payments : [],
    prepayments: scheduleMatches ? payments.filter((part) => part.trigger === 'signing') : [],
    scheduleMatches,
  };
}

// --- готовность -----------------------------------------------------------------------------------------------

/** Виды текстов, без опубликованной версии которых договор не собрать. */
export type ContractTemplateKind = 'offer' | 'order' | 'specification' | 'invoice';

/** Реквизиты студии, без которых документ не формируется (карта интерфейса, 9.6). */
export type StudioRequisite =
  | 'fullName'
  | 'inn'
  | 'kpp'
  | 'ogrn'
  | 'legalAddress'
  | 'bankName'
  | 'bankBik'
  | 'bankCorrespondentAccount'
  | 'bankAccount'
  | 'signerName'
  | 'signerPosition'
  | 'signerBasis';

/** Реквизиты клиента для заявки: у организации — с руководителем и основанием, у ИП — без них. */
export type ClientRequisite = 'inn' | 'legalName' | 'address' | 'directorName' | 'directorBasis';

/** Проблема готовности: что не так и что дозаполнить на месте. */
export type ContractIssue =
  | { readonly code: 'proposal_not_accepted' }
  | { readonly code: 'template_not_published'; readonly kind: ContractTemplateKind }
  | { readonly code: 'studio_requisites'; readonly fields: StudioRequisite[] }
  | { readonly code: 'studio_signer' }
  | { readonly code: 'client_requisites'; readonly fields: ClientRequisite[] }
  | { readonly code: 'party_signer' }
  | { readonly code: 'party_signer_email' }
  | { readonly code: 'party_signer_phone' };

export type ContractIssueCode = ContractIssue['code'];

/** Клиент сделки для готовности: вид и реквизиты. */
export interface ReadinessClient {
  readonly kind: 'legal' | 'entrepreneur' | 'person';
  readonly inn: string | null;
  readonly legalName: string | null;
  readonly address: string | null;
  readonly directorName: string | null;
  readonly directorBasis: string | null;
}

/** Подписант клиента: контакт с отметкой «Подписывает» (у физлица — сам клиент). */
export interface ReadinessSigner {
  readonly email: string | null;
  readonly phone: string | null;
}

export interface ReadinessInput {
  readonly accepted: AcceptedProposal | null;
  /** Опубликованные тексты: вид → есть действующая версия. */
  readonly published: Readonly<Record<ContractTemplateKind, boolean>>;
  readonly studio: Pick<StudioSnapshot, 'legalForm' | StudioRequisite>;
  /** Подписант за студию указан в реквизитах и действует. */
  readonly studioSigner: boolean;
  readonly client: ReadinessClient;
  readonly signer: ReadinessSigner | null;
}

const blank = (value: string | null | undefined): boolean => (value ?? '').trim() === '';

const STUDIO_ALWAYS: readonly StudioRequisite[] = [
  'fullName',
  'inn',
  'ogrn',
  'legalAddress',
  'bankName',
  'bankBik',
  'bankCorrespondentAccount',
  'bankAccount',
];
/** У организации в преамбуле — подписант с должностью и основанием; у ИП подписывает он сам. */
const STUDIO_LEGAL: readonly StudioRequisite[] = ['kpp', 'signerName', 'signerPosition', 'signerBasis'];

/** Чего не хватает в реквизитах студии для документов с клиентом. */
export function missingStudioRequisites(studio: ReadinessInput['studio']): StudioRequisite[] {
  const wanted = studio.legalForm === 'legal' ? [...STUDIO_ALWAYS, ...STUDIO_LEGAL] : STUDIO_ALWAYS;
  return wanted.filter((field) => blank(studio[field]));
}

/** Чего не хватает у клиента: у организации — ИНН, наименования, адреса, руководителя и основания; у ИП — без двух последних. */
export function missingClientRequisites(client: ReadinessClient): ClientRequisite[] {
  if (client.kind === 'person') return [];
  const wanted: readonly ClientRequisite[] =
    client.kind === 'legal'
      ? ['inn', 'legalName', 'address', 'directorName', 'directorBasis']
      : ['inn', 'legalName', 'address'];
  return wanted.filter((field) => blank(client[field]));
}

/**
 * Готовность договора (раздел 5): список проблем по порядку экрана — КП, тексты, студия, клиент, подписант. С
 * проблемами пакет не уходит (`documents.not_ready`); каждая дозаполняется на месте.
 */
export function contractIssues(input: ReadinessInput): ContractIssue[] {
  const issues: ContractIssue[] = [];
  if (input.accepted === null) issues.push({ code: 'proposal_not_accepted' });
  const needsInvoice = input.accepted !== null && orderTerms(input.accepted).prepayments.length > 0;
  const kinds: readonly ContractTemplateKind[] = needsInvoice
    ? ['offer', 'order', 'specification', 'invoice']
    : ['offer', 'order', 'specification'];
  for (const kind of kinds) if (!input.published[kind]) issues.push({ code: 'template_not_published', kind });
  const studio = missingStudioRequisites(input.studio);
  if (studio.length > 0) issues.push({ code: 'studio_requisites', fields: studio });
  if (!input.studioSigner) issues.push({ code: 'studio_signer' });
  const client = missingClientRequisites(input.client);
  if (client.length > 0) issues.push({ code: 'client_requisites', fields: client });
  if (input.signer === null) issues.push({ code: 'party_signer' });
  else {
    if (blank(input.signer.email)) issues.push({ code: 'party_signer_email' });
    // У физлица в заявке — ФИО, почта и телефон (решение владельца 10: паспорт не нужен).
    if (input.client.kind === 'person' && blank(input.signer.phone))
      issues.push({ code: 'party_signer_phone' });
  }
  return issues;
}

// --- счета и акты по графику ----------------------------------------------------------------------------------

/** Счёт или акт сделки, уже выставленный по доле графика (отозванные не считаются). */
export interface IssuedByPart {
  readonly kind: 'invoice' | 'act';
  /** Доля графика (с нуля); null — акт не по доле, а по этапу или на свою сумму. */
  readonly part: number | null;
  readonly amount: Kopecks;
  readonly void: boolean;
}

/** Строка блока «Счета и акты»: доля графика, сколько по ней выставлено счетов и актов. */
export interface PaymentRow extends OrderPayment {
  readonly invoices: number;
  readonly acts: number;
}

/** Строки графика подписанной заявки с выставленными по ним счетами и актами (отозванные не считаются). */
export function paymentRows(
  payments: readonly OrderPayment[],
  issued: readonly IssuedByPart[],
): PaymentRow[] {
  const live = issued.filter((doc) => !doc.void);
  return payments.map((part) => ({
    ...part,
    invoices: live.filter((doc) => doc.kind === 'invoice' && doc.part === part.index).length,
    acts: live.filter((doc) => doc.kind === 'act' && doc.part === part.index).length,
  }));
}

/** Повторный счёт или акт на ту же долю — предупреждение в окне, а не запрет. */
export function duplicateOnPart(
  issued: readonly IssuedByPart[],
  kind: 'invoice' | 'act',
  part: number | null,
): boolean {
  return part !== null && issued.some((doc) => !doc.void && doc.kind === kind && doc.part === part);
}

/** Допсоглашение к заявке: новая сумма (если менялась цена) и подписано ли оно. */
export interface AmendmentTotal {
  readonly amount: Kopecks | null;
  readonly signed: boolean;
  /** Порядковый номер «№ N к заявке». */
  readonly n: number;
}

/**
 * Сумма договора: заявка, а если подписанные допсоглашения меняли цену — новая сумма последнего из них (по номеру).
 * Неподписанные и отозванные допсоглашения сумму не меняют.
 */
export function contractTotal(order: Kopecks, amendments: readonly AmendmentTotal[]): Kopecks {
  let total = order;
  let latest = 0;
  for (const amendment of amendments) {
    if (!amendment.signed || amendment.amount === null || amendment.n < latest) continue;
    total = amendment.amount;
    latest = amendment.n;
  }
  return total;
}

/**
 * Не больше ли акты суммы договора (`documents.acts_exceed_order`): действующие акты (подписанные и на подписи) вместе
 * с новым — не больше суммы заявки с допсоглашениями.
 */
export function actsExceedOrder(total: Kopecks, issued: readonly IssuedByPart[], amount: Kopecks): boolean {
  const acts = issued
    .filter((doc) => doc.kind === 'act' && !doc.void)
    .reduce((sum, doc) => sum + doc.amount, 0);
  return acts + amount > total;
}

/** Сколько ещё можно закрыть актами: сумма договора минус действующие акты, не меньше нуля. */
export function actsRemaining(total: Kopecks, issued: readonly IssuedByPart[]): Kopecks {
  const acts = issued
    .filter((doc) => doc.kind === 'act' && !doc.void)
    .reduce((sum, doc) => sum + doc.amount, 0);
  return kopecks(Math.max(total - acts, 0));
}

/** Номер следующего допсоглашения к заявке: «№ N к заявке З-…» — по порядку, отозванные номера не переиспользуются. */
export const nextAmendmentN = (existing: readonly { readonly n: number }[]): number =>
  existing.reduce((max, own) => Math.max(max, own.n), 0) + 1;

/** Этап проекта для графика оплат: название у нас, название для клиента (как в КП) и закрыт ли он. */
export interface StageProgress {
  readonly title: string;
  readonly clientTitle: string | null;
  readonly done: boolean;
}

/**
 * Когда доля графика «созрела» к счёту: `due` — пора выставлять, `waiting` — ещё рано (ждём этап или запуск),
 * `monthly` — ежемесячная, выставляется каждый месяц.
 */
export type PartReadiness = 'due' | 'waiting' | 'monthly';

const sameTitle = (a: string, b: string): boolean =>
  a.trim().toLocaleLowerCase('ru') === b.trim().toLocaleLowerCase('ru');

/**
 * Этап проекта (номер с нуля) доли «со стартом этапа»: этап из КП ищется по названию (клиентскому или нашему); доля
 * без этапа — середина проекта, как в КП; этап не нашёлся (переименовали) — по порядку среди долей «со стартом этапа».
 * Этапов нет — null. Общее правило графика оплат («Пора выставить») и условия оплаты этапа (подпроект 8).
 */
export function stageIndexOfPart(
  part: Pick<OrderPayment, 'stage' | 'index'>,
  payments: readonly Pick<OrderPayment, 'trigger' | 'index'>[],
  stages: readonly Pick<StageProgress, 'title' | 'clientTitle'>[],
): number | null {
  if (stages.length === 0) return null;
  const index =
    part.stage === null
      ? Math.floor(stages.length / 2)
      : stages.findIndex(
          (stage) =>
            sameTitle(stage.title, part.stage ?? '') ||
            (stage.clientTitle !== null && sameTitle(stage.clientTitle, part.stage ?? '')),
        );
  if (index >= 0) return index;
  const order = payments
    .filter((own) => own.trigger === 'stage_start')
    .findIndex((own) => own.index === part.index);
  return Math.min(Math.max(order, 0), stages.length - 1);
}

/**
 * Готовность доли графика к счёту по ходу проекта. «При подписании» — сразу. «Со стартом этапа» — этап доли
 * (`stageIndexOfPart`); первый этап стартует с проектом, следующий — когда закрыт предыдущий. «При запуске» — когда
 * закрыты все этапы или проект сдаётся.
 */
export function partReadiness(
  part: Pick<OrderPayment, 'trigger' | 'stage' | 'index'>,
  payments: readonly Pick<OrderPayment, 'trigger' | 'index'>[],
  progress: { readonly stages: readonly StageProgress[]; readonly delivered: boolean },
): { readonly state: PartReadiness; readonly stage: string | null } {
  const { stages, delivered } = progress;
  const allDone = stages.length > 0 && stages.every((stage) => stage.done);
  switch (part.trigger) {
    case 'signing':
      return { state: 'due', stage: null };
    case 'monthly':
      return { state: 'monthly', stage: null };
    case 'launch':
      return { state: delivered || allDone ? 'due' : 'waiting', stage: null };
    case 'stage_start': {
      const index = stageIndexOfPart(part, payments, stages);
      if (index === null) return { state: delivered ? 'due' : 'waiting', stage: null };
      const started = delivered || stages.slice(0, index).every((stage) => stage.done);
      const stage = stages[index];
      return {
        state: started ? 'due' : 'waiting',
        stage: stage === undefined ? part.stage : (stage.clientTitle ?? stage.title),
      };
    }
  }
}

/**
 * Должности в начале имени контакта, за которыми стоит сам подписант: так записывают контакт из заявки, когда имени
 * не знают («Руководитель», «Генеральный директор»). Заместителей, секретарей и бухгалтеров здесь нет.
 */
const DIRECTOR_WORDS: ReadonlySet<string> = new Set([
  'руководитель',
  'директор',
  'генеральный',
  'гендиректор',
  'исполнительный',
  'владелец',
  'собственник',
  'учредитель',
  'председатель',
  'президент',
  'ceo',
  'director',
  'owner',
]);

/** Подразделение в должности («руководитель отдела продаж») — это другой человек, не руководитель организации. */
const UNIT_WORDS = /^(отдел|департамент|служб|направлени|подразделени|филиал|проект|группы|группа)/;

/**
 * Имя контакта — должность руководителя, а не человек: пустое, или начинается со слова-должности руководителя и не
 * называет подразделение. Секретарь, бухгалтер, «руководитель отдела» — другие люди: их имя не подменяется.
 */
export function looksLikeRole(name: string): boolean {
  const words = name
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.replace(/[^a-zа-яё]/g, ''))
    .filter((word) => word !== '');
  const first = words[0];
  if (first === undefined) return true;
  return DIRECTOR_WORDS.has(first) && !words.some((word) => UNIT_WORDS.test(word));
}

/**
 * ФИО того, кто подписывает документ за клиента: руководитель организации из реквизитов или сам предприниматель
 * (имя ИП без «ИП»). У частного лица — нет: подписывает он сам, его имя и есть контакт.
 */
export function clientSignatoryName(client: {
  readonly kind: 'legal' | 'entrepreneur' | 'person';
  readonly name: string;
  readonly requisites: { readonly legalName: string | null; readonly directorName: string | null };
}): string | null {
  if (client.kind === 'legal') return client.requisites.directorName;
  if (client.kind === 'person') return null;
  const own = (client.requisites.legalName ?? client.name)
    .replace(/^\s*(индивидуальный предприниматель|ип)\s+/i, '')
    .trim();
  return own === '' ? null : own;
}

/**
 * Имя подписанта клиента в пакете — к нему обращаются письма подписания и согласие на странице подписи: ФИО
 * выбранного контакта. Если контакт записан должностью руководителя («Руководитель»), а ФИО того, кто подписывает
 * за клиента, известно (руководитель из реквизитов, сам ИП), — это ФИО.
 */
export function partySignerName(contactName: string, signatoryName: string | null): string {
  const own = contactName.trim();
  const signatory = signatoryName?.trim() ?? '';
  return looksLikeRole(own) && signatory !== '' ? signatory : own;
}

// --- сроки в рабочих днях ---------------------------------------------------------------------------------------

/** Рабочих дней в неделе: недели КП переводятся в рабочие дни. */
export const WORK_DAYS_PER_WEEK = 5;

/**
 * Срок заявки в рабочих днях (решение владельца 2026-10-10): по плану, вверх до целой недели — в пользу студии и в
 * согласии с «≈ N недель» в КП. У КП без этапов срока нет.
 */
export function orderTermDays(accepted: Pick<AcceptedProposal, 'package'>): number | null {
  const pack = accepted.package;
  if (pack === null) return null;
  const days = pack.termDays ?? (pack.termWeeks === null ? null : pack.termWeeks * WORK_DAYS_PER_WEEK);
  if (days === null) return null;
  return Math.max(1, Math.ceil(days / WORK_DAYS_PER_WEEK)) * WORK_DAYS_PER_WEEK;
}

/**
 * Рабочие дни этапа для спецификации: по плану; у старых КП — из недель целиком (неделя N — дни 5N−4…5N), то есть
 * не короче обещанного в КП.
 */
export function stageWorkDays(stage: AcceptedStage): { readonly from: number; readonly to: number } | null {
  if (stage.days !== undefined && stage.days !== null) return stage.days;
  if (stage.weeks === null) return null;
  return workDaysOfWeekRange(stage.weeks);
}
